import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import useAddManualSizeBiasProduct from "@babackapp/private-api-client/client/use-add-manual-size-bias-product";
import useSearchManualSizeBiasCandidateProducts from "@babackapp/private-api-client/client/use-search-manual-size-bias-candidate-products";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import { Input } from "@babackapp/ui/components/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@babackapp/ui/components/popover";
import { Loader2, Plus, Search, ShoppingCart } from "lucide-react";
import { useState } from "react";
import { useDebouncedCallback } from "use-debounce";

const DEFAULT_ADDED_BIAS = "true_to_size";

export function AddManualBiasProductCombobox() {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const debouncedSetQ = useDebouncedCallback(setDebouncedQ, 250);

  const { data: products = [], isFetching } =
    useSearchManualSizeBiasCandidateProducts({
      shopId: shop.id,
      q: debouncedQ,
      enabled: open,
    });

  const { mutate: addProduct } = useAddManualSizeBiasProduct(shop.id);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          setSearch("");
          setDebouncedQ("");
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm">
          <Plus className="size-4" />
          {t("sizing.manualBias.addProduct")}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="start">
        <div className="p-2 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              autoFocus
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                debouncedSetQ(e.target.value);
              }}
              placeholder={t("sizing.manualBias.searchPlaceholder")}
              className="pl-9"
            />
          </div>
        </div>
        <div className="max-h-72 overflow-y-auto">
          {isFetching && (
            <div className="flex items-center justify-center py-6 text-sm text-muted-foreground gap-2">
              <Loader2 className="size-4 animate-spin" />
              {t("misc.loading")}
            </div>
          )}
          {!isFetching &&
            debouncedQ.trim().length > 0 &&
            products.length === 0 && (
              <div className="py-6 text-center text-sm text-muted-foreground">
                {t("productSelect.noResult")}
              </div>
            )}
          {!isFetching &&
            products.map((product) => (
              <Button
                key={product.productId}
                type="button"
                variant="ghost"
                onClick={() => {
                  addProduct({
                    productId: product.productId,
                    manualSizeBias: DEFAULT_ADDED_BIAS,
                  });
                  setOpen(false);
                  setSearch("");
                  setDebouncedQ("");
                }}
                className="flex items-center gap-3 w-full h-auto justify-start px-3 py-2 rounded-none border-b last:border-b-0"
              >
                {product.imageUrl ? (
                  <img
                    src={product.imageUrl}
                    alt=""
                    className="size-8 rounded object-cover shrink-0"
                  />
                ) : (
                  <div className="size-8 rounded bg-muted flex items-center justify-center shrink-0">
                    <ShoppingCart className="size-4 text-muted-foreground" />
                  </div>
                )}
                <span className="text-sm truncate font-normal">
                  {product.title}
                </span>
              </Button>
            ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
